import React, {
    useContext, useCallback, useState, useEffect, useMemo
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ModalNextProvider, Loader, Icon, FormattedDate, ModalService
} from '@jutro/components';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { withRouter, Link } from 'react-router-dom';
import { LobIconUtil } from 'gw-portals-util-js';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import moment from 'moment';
import { getNormalizedLOBName } from 'gw-portals-config-js';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import ReactGA from 'react-ga4';
// import { ButtonLink } from '@jutro/router';
import Claim from '../../models/Claim';
import styles from './FNOLSelectPolicy.module.scss';
import metadata from './FNOLSelectPolicy.metadata.json5';
import messages from '../../FNOL.messages';
import { getFontIcon, getProductName } from '../../../../utils/lobIconUtil';
import ContactUsPageTemplate from '../../../gw-capability-common-components/ContactUsPage/ContactUsPageTemplate';
import { Link as HyperLink } from 'gw-components-platform-react';
import claimDetailsStyles
    from '../../../nexus-capability-claim-react/pages/ClaimDetails/ClaimDetails.module.scss';

function FNOLSelectPolicy(props) {
    const {
        wizardData: claimVM,
        updateWizardData,
        history,
        basePath,
        steps,
        goNext,
        history: {
            location: { state }
        }
    } = props;
    const {
        accountNumber: accountNumberFromPrevPage,
        policyNumber: policyNumberFromPrevPage,
        redirectPath,
        claimNumber,
        claimStatus
    } = state || {};
    const { FNOLService } = useDependencies('FNOLService');
    const { ClaimService } = useDependencies('ClaimService');
    const { AccountService } = useDependencies('AccountService');
    const translator = useContext(TranslatorContext);
    const [policyData, setPolicyData] = useState({});
    const [policyNumber, setPolicyNumber] = useState(policyNumberFromPrevPage);
    const [accountNumber, setAccountNumber] = useState(accountNumberFromPrevPage);
    const [isLoading, setIsLoading] = useState(true);
    const [isShowInfoMessage, setInfoMessage] = useState(false);
    const { authHeader } = useAuthentication();
    const { isComponentValid, registerComponentValidation } = useValidation('FNOLSelectPolicy');
    const [reportDamageToProperty, setReportDamageToProperty] = useState(true);
    const [isNonAutoPolicy, setIsNonAutoPolicy] = useState(false);
    const intl = useContext(IntlContext);

    const getFormattedDate = useCallback((items, index, property) => {
        if (typeof items[property.id] === 'undefined') {
            return '';
        }
        return <FormattedDate value={new Date(items[property.id])} format="short" />;
    }, []);

    const processResponseData = useCallback((accountDetails) => {
        const policyDetails = [];
        if (appConfig.persona === 'policyholder') {
            accountDetails.filter((item) => (item.policyType !== 'BusinessFleet' && item.policyType !== 'GA7Garage' && item.policyType !== 'PersonalUmbrella'))
                .forEach((policyInfo) => {
                    const policy = {
                        product: policyInfo.policyNumber,
                        policyNumber: policyInfo.policyNumber,
                        productType: policyInfo.policyType,
                        effectiveDate: policyInfo.effectiveDate,
                        expirationDate: policyInfo.expirationDate
                    };
                    policyDetails.push(policy);
                });
        } else {
            const lobindex = 0;
            const allowedPolicy = appConfig.allowedPolicyTypes;
            accountDetails.policySummaries.forEach((policyInfo) => {
                const { isCancelled } = policyInfo;
                const businessLines = policyInfo.policyLines;
                const { productName } = policyInfo.product;
                const businessLine = businessLines[lobindex].lineOfBusinessCode;
                if (
                    productName !== 'Commercial Package'
                    && _.includes(allowedPolicy, businessLine)
                    && !isCancelled
                    && !moment().isBefore(moment(policyInfo.effective))
                ) {
                    const policy = {
                        product: policyInfo.policyNumber,
                        policyNumber: policyInfo.policyNumber,
                        productType: businessLine,
                        effectiveDate: policyInfo.effectiveDate,
                        expirationDate: policyInfo.expirationDate
                    };
                    policyDetails.push(policy);
                }
            });
        }
        return policyDetails;
    }, []);

    const getPolicyDetails = useCallback(
        (selectedAccountNumber) => {
            if (appConfig.persona === 'policyholder') {
                const policySearchCriteria = {
                    lossDate: _.get(claimVM.value, 'lossDate')
                };
                FNOLService.searchPolicies(policySearchCriteria, authHeader).then((response) => {
                    setPolicyData(processResponseData(response));
                    setIsLoading(false);
                });
            } else {
                AccountService.getAccountDetails(selectedAccountNumber, authHeader).then(
                    (response) => {
                        setPolicyData(processResponseData(response));
                        setIsLoading(false);
                    }
                );
            }
        },
        [AccountService, FNOLService, authHeader, claimVM.value, processResponseData]
    );

    const redirectDraftClaimFlow = useCallback(() => {
        setIsLoading(true);
        if (_.isFunction(ClaimService.addRecentlyViewedClaim)) {
            ClaimService.addRecentlyViewedClaim(claimNumber, authHeader);
        }
        return FNOLService.getClaim(claimNumber, authHeader).then((getClaimResponse) => {
            setPolicyNumber(getClaimResponse.policy.policyNumber);
            claimVM.value = getClaimResponse;
            const normalizedLobCode = getNormalizedLOBName(claimVM.value.policy.policyType);
            return history.push({
                pathname: `/fnol-${normalizedLobCode}`,
                state: {
                    claimData: getClaimResponse,
                    redirectPath: redirectPath,
                    claimStatus: claimStatus
                }
            });
        });
    }, [
        ClaimService,
        claimNumber,
        authHeader,
        FNOLService,
        claimVM.value,
        history,
        redirectPath,
        claimStatus
    ]);

    useEffect(() => {
        const { googleAnalytics } = appConfig;
        if (googleAnalytics.tracking) {
            ReactGA.send({ hitType: 'pageview', page: '/fnolSelectPolicy', title: 'FNOL Start Page' });
        }
    }, []);

    useEffect(() => {
        const previousAccountNumber = _.get(claimVM.value, 'policy.accountNumber');

        const policyNumberVar = _.isNil(policyNumber)
            ? _.get(claimVM.value, 'policy.policyNumber')
            : policyNumber;
        setPolicyNumber(policyNumberVar);
        setAccountNumber(accountNumberFromPrevPage || previousAccountNumber);

        const flowCheck = redirectPath.split('/');
        if (
            (flowCheck[1] === 'accounts'
                && claimStatus
                !== translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'draft' })
                && (!_.isEmpty(accountNumberFromPrevPage) || !_.isEmpty(previousAccountNumber)))
            || appConfig.persona === 'policyholder'
        ) {
            getPolicyDetails(accountNumberFromPrevPage || previousAccountNumber);
        } else {
            setIsLoading(false);
        }
        // This will call only once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!_.isEmpty(claimNumber)) {
            redirectDraftClaimFlow();
        }
        const stepsWithoutSelectPolicy = steps.slice(1, steps.length);
        if (
            basePath !== '/fnol-select-policy'
            && !stepsWithoutSelectPolicy.some((step) => step.visited)
        ) {
            goNext();
        }
        // This will call only once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changePolicy = useCallback(() => {
        const previousLossDate = _.get(claimVM.value, 'lossDate');
        claimVM.value = new Claim();
        _.set(claimVM.value, 'lossDate', previousLossDate);
        updateWizardData(claimVM);
    }, [claimVM, updateWizardData]);

    const callSearchPolicyService = useCallback(() => {
        const policySearchCriteria = {
            policyNumber: policyNumber,
            lossDate: _.get(claimVM.value, 'lossDate')
        };
        return FNOLService.searchPolicies(policySearchCriteria, authHeader).then(
            (policyResponse) => {
                if (_.isEmpty(policyResponse) && basePath === '/fnol-select-policy') {
                    setInfoMessage(true);
                    return claimVM;
                }
                if (_.isEmpty(policyResponse)) {
                    setInfoMessage(true);
                    return false;
                }
                _.set(claimVM.value, 'policy', policyResponse[0]);
                _.set(claimVM.value, 'policy.accountNumber', accountNumber);
                const normalizedLobCode = getNormalizedLOBName(claimVM.value.policy.policyType);
                return history.push({
                    pathname: `/fnol-${normalizedLobCode}`,
                    state: {
                        claimData: claimVM.value,
                        redirectPath: redirectPath
                    }
                });
            }
        );
    }, [
        policyNumber,
        claimVM,
        FNOLService,
        authHeader,
        basePath,
        accountNumber,
        history,
        redirectPath
    ]);

    const onNext = useCallback(() => {
        const prevPolicyNo = _.get(claimVM.value, 'policy.policyNumber');
        if (!_.isEmpty(prevPolicyNo) && !_.isEmpty(policyNumber) && prevPolicyNo !== policyNumber) {
            return ModalNextProvider.showConfirm({
                title: messages.policyChangeModalTitle,
                message: messages.policyChangeModalMessage,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.changePolicyButtonlabel,
                cancelButtonText: messages.fnolMessagePopupCancel
            }).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                changePolicy();
                return callSearchPolicyService();
            }, _.noop);
        }
        return callSearchPolicyService();
    }, [callSearchPolicyService, changePolicy, claimVM.value, policyNumber]);

    const updatePolicyType = useCallback(() => {
        if (!_.isEmpty(policyData)) {
            const foundPolicy = policyData.find((element) => element.policyNumber === _.get(claimVM.value, 'selectedPolicy'));
            // setIsNonAutoPolicy(foundPolicy.productType !== 'BusinessAuto');
            setIsNonAutoPolicy(true);
        }
    });


    const onPolicySelect = useCallback(
        (selectedValue) => {
            if (_.get(claimVM.value, 'policy.accountNumber')) {
                setAccountNumber(_.get(claimVM.value, 'policy.accountNumber'));
            }
            _.set(claimVM.value, 'selectedPolicy', selectedValue);
            updateWizardData(claimVM);
            setPolicyNumber(selectedValue);
            updatePolicyType();
        },
        [claimVM, updatePolicyType, updateWizardData]
    );

    const getCell = useCallback(
        (item, index, property) => {
            return <span>{item[property.id]}</span>;
        },
        []
    );

    const generateDataForRadioButton = useCallback((row) => {
        return {
            code: row.policyNumber,
            value: ''
        };
    }, []);

    const getProductImage = useCallback((item) => {
        const icon = LobIconUtil.getFontIcon(item.productType);
        return (
            <Icon
                icon={icon}
                title={item.productCode}
            />
        );
    }, []);

    const dateIsInFuture = useCallback((date) => {
        if (!date) {
            return false;
        }

        const currentTime = new Date();
        const selectedDate = new Date(date);
        return selectedDate > currentTime;
    }, []);

    const isPageValid = useCallback(() => {
        if (_.isEmpty(policyNumber) && _.isEmpty(_.get(claimVM.value, 'policy.policyNumber'))) {
            return false;
        }
        if (!_.get(claimVM.value, 'lossDate') || dateIsInFuture(_.get(claimVM.value, 'lossDate'))) {
            return false;
        }
        if (isNonAutoPolicy) {
            return reportDamageToProperty;
        }
        return true;
    }, [claimVM.value, dateIsInFuture, policyNumber, isNonAutoPolicy, reportDamageToProperty]);

    useEffect(() => {
        registerComponentValidation(isPageValid);
    }, [isPageValid, registerComponentValidation]);

    const getPolicyNumberHeading = useCallback(() => {
        const previousPolicyNumber = _.get(claimVM.value, 'policy.policyNumber');
        const policyNumberToDisplay = policyNumber || previousPolicyNumber || '';
        return `${translator(messages.lossInformation)} ${policyNumberToDisplay}`;
    }, [claimVM.value, translator, policyNumber]);

    const handleToggleSelect = (value) => {
        setReportDamageToProperty(value);
    };

    useEffect(() => {
        if (!_.isUndefined(_.get(claimVM.value, 'selectedPolicy'))) {
            updatePolicyType();
        }
        if (!_.isEmpty(claimVM.value.policy) && !_.isEmpty(claimVM.value.policy.policyNumber)) {
            setIsNonAutoPolicy(true);
        }
    }, [claimVM.value, policyData, policyNumber, updatePolicyType]);

    /* const getProdImgPath = (product) => {
        let imagePath;
        switch(product) {
            case 'PersonalAuto':
                imagePath = '/lob-images/personal-auto.png';
                break;
            case 'BusinessAuto':
                imagePath = '/lob-images/commercial-auto.png';
                break;
            case 'Homeowners':
            case 'HOPHomeowners':
                imagePath = '/lob-images/home-owners.png';
                break;
            case 'CommercialProperty':
                imagePath = '/lob-images/commercial-property.png';
                break;
            case 'PersonalUmbrellaLine':
                imagePath = '/lob-images/umbrella.png';
                break;
            default:
                imagePath = '/lob-images/no-icon.png';
        }
        return imagePath;
    }*/


    const getAvailablePolicies = useMemo(() => {
        if (!_.isEmpty(policyData)) {
            const values = policyData.map((details) => {
                return {
                    displayName: <div>{getProductName(details.productType)}<br/>Policy: {details.policyNumber}</div>,
                    iconName: getFontIcon(details.productType),
                    secondaryLabel: `Effective: ${intl.formatDate(details.effectiveDate, { year: 'numeric', month: 'short', day: 'numeric' })} 
                     Expiry: ${intl.formatDate(details.expirationDate, { year: 'numeric', month: 'short', day: 'numeric' })}`,
                    id: details.policyNumber
                };
            });
            return values;
        }
        return [];
    }, [intl, policyData]);

    const phoneToTrigger = (event, agentPhone) => {
        event.preventDefault();
        window.location = `tel:${agentPhone}`;
    };


    const getPhoneLink = (agentPhoneNumber) => {
        const agentPhone = agentPhoneNumber;
        if (!agentPhone) {
            return null;
        }
        return (
            <HyperLink className={styles.hyperlink} onClick={(e) => phoneToTrigger(e, agentPhone)} href="/">
                {agentPhone}
            </HyperLink>
        );
    };

    const mailToTrigger = useCallback((event, email) => {
        event.preventDefault();
        window.location = `mailto:${email}`;
    }, []);


    const getPolicyNotPresentLink = useMemo(() => {
        /*return (
            <span>
                {translator(messages.policyNotListed)}
                {' '}
                {appConfig.adjusterPhoneNumber} or email&nbsp;
                <Link>
                    <b>{appConfig.adjusterEmail}</b>
                </Link>
            </span>
        );*/
        return (
            <span>
                {translator(messages.policyNotListed)}
                {' '}
                <Link onClick={(e) => phoneToTrigger(e, appConfig.adjusterPhoneNumber)} href="/">
                    {appConfig.adjusterPhoneNumber}
                </Link>
                &nbsp; or email&nbsp;
                <Link className={styles.link} onClick={(e) => mailToTrigger(e, appConfig.adjusterEmail)} href="/">
                    {appConfig.adjusterEmail}
                </Link>
            </span>

        );
    }, [translator]);

    const getPolicyNotPresentMessage = useMemo(() => {
        return (
            <span>
                {translator(messages.policyNotListedMsg)}
                {' '}
                {appConfig.adjusterPhoneNumber}
            </span>

            /*  <Link onClick={(event) => openContactPopup()} href="/" className={styles.textBlack}>
                    <b>{translator(messages.fnolMyPolicyNotHere)}</b>
                </Link> */

        );
    }, [translator]);

    const openContactPopup = useCallback(
        () => {
            event.preventDefault();
            /* ModalNextProvider.showAlert({
                title: 'Contct Us',
                message: 'will see',
                status: 'info',
                icon: 'mi-error-outline'
            });*/
            ModalService.showInfo({
                title: translator(messages.contactUs),
                message: <ContactUsPageTemplate />
            });
        },
        [translator]
    );

    const getClaimsThirdPartyLink = useMemo(() => {
        return (
            <Link onClick={(event) => openContactPopup()} href="/" className={styles.textBlack}>
                <b>{translator(messages.fnolReadAboutClaims)}</b>
            </Link>
        );
    }, [openContactPopup, translator]);

    const getClaimNoteMessage = useMemo(() => {
        return (
            <span>
                {translator(messages.makeClaimNote1)}
                <b><u>emergency</u></b>&nbsp;
                {translator(messages.makeClaimNote2, { phoneNo: appConfig.adjusterPhoneNumber })}
            </span>
        );
    }, [translator]);


    const overrides = {
        policyTable: {
            data: policyData
        },
        policyTableHeadingId: {
            visible: policyData.length > 0
        },
        policyLossDateHeadingId: {
            content: getPolicyNumberHeading()
        },
        /* policySelectRadio: {
            option: generateDataForRadioButton,
            value:
                policyNumber
                || (!_.isEmpty(claimVM.value) && _.get(claimVM.value, 'policy.policyNumber')),
            onValueChange: onPolicySelect
        },*/
        noPolicyInfoLableId: {
            visible: isShowInfoMessage
        },
        lossDateId: {
            defaultValue: new Date()
        },
        nonAutoDamageReportToggle: {
            value: reportDamageToProperty,
            visible: isNonAutoPolicy
        },
        policyNotPresentMessage: {
            content: getPolicyNotPresentMessage,
            visible: policyData && policyData.length < 1
        },
        whichPolicyAreYouFilingClaim: {
            visible: policyData && policyData.length > 0
        },
        radiobuttoncard: {
            visible: policyData && policyData.length > 0,
            availableValues: getAvailablePolicies,
            value:
                policyNumber
                || (!_.isEmpty(claimVM.value) && _.get(claimVM.value, 'policy.policyNumber')),
            onValueChange: onPolicySelect
        },
        policyNotPresentId: {
            // content: getPolicyNotPresentLink
            // visible: policyData && policyData.length > 0,
            content: getPolicyNotPresentLink,
        },
        thirdPartyLinkId: {
            content: getClaimsThirdPartyLink
        },
        claimNoteInfoLabelId: {
            content: getClaimNoteMessage
        }
    };
    const resolvers = {
        resolveCallbackMap: {
            getCell: getCell,
            getFormattedDate: getFormattedDate,
            getProductImage: getProductImage,
            handleToggleSelect
        },
        resolveClassNameMap: styles
    };
    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }
    return (
        <WizardPage
            onNext={onNext}
            showPrevious={false}
            disableNext={!isComponentValid}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

FNOLSelectPolicy.propTypes = wizardProps;
FNOLSelectPolicy.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};

export default withRouter(FNOLSelectPolicy);
